import envVars from './config';
import { isExternalSdk } from './environment-utils';
import { DebugLog } from './debug-log';

let Raven: any = (window as any).Raven;
let shouldSendDatas = true;
let tags: Record<string, any>;
let mraidInfos: MraidInfos = {
  mraidState: 'unknown',
  mraidIsViewable: 'undefined'
};

function getMajorVersion(sdkVersion: string = ''): string {
  const sdkVersionDetail = sdkVersion.split('.');
  return (sdkVersionDetail && sdkVersionDetail[0]) || 'unknown';
}

function isFormatError(url: string): boolean {
  const formatsUriContains = [
    'ogyfmts.com',
    'litecdn.com',
    'presage.io',
    'ogury.io',
    'mraid-wrapper'
  ];
  for (let i = 0; i < formatsUriContains.length; i++) {
    if (url.indexOf(formatsUriContains[i]) !== -1) {
      return true;
    }
  }
  return false;
}

function getSentryIgnoreUrls(): RegExp[] {
  return [/confiant-qa\.com/];
}

function getSentryIgnoreErrors(): RegExp[] {
  return [
    /The video player does not support autoplay/,
    /Video failed to start playing/,
    /No prepared event timeout for video/,
    /Problem displaying a media file/,
    /\[object Event\]/,
    /Java exception was raised during method invocation/,
    /Java object is gone/,
    /NPObject/,
    /Unexpected number/,
    /Unexpected identifier/,
    /MoatMAK is not defined/,
    /mraid is not defined/,
    /Invalid or unexpected token/,
    /mraidbridge is not defined/,
    /Cannot read property 'contentDocument' of undefined/,
    /webviewDidAppear is not defined/,
    /webviewDidClose is not defined/,
    /window.onReceive is not a function/,
    /HTML5 Video player error/,
    /Could not find a media file supported by the video player/,
    /prefetch video error/,
    /MRAID container error/,
    /onCustomEvent is not defined/
  ];
}

function getSentryUrl(formatName: FormatName): string | undefined {
  if (
    (formatName === 'Video' || formatName === 'AdChooser') &&
    envVars.SENTRY_URL_VIDEO_FORMATS
  ) {
    return envVars.SENTRY_URL_VIDEO_FORMATS;
  }

  return envVars.SENTRY_URL_UNIVERSAL_FORMATS;
}

function getSampleRate(formatName: FormatName): number {
  return formatName === 'Video' ||
    formatName === 'MRAID' ||
    formatName === 'AdChooser'
    ? 0.05
    : 0.2;
}

export function initRaven(
  format: FormatName = 'unknown',
  context?: AdContext
): void {
  // We exclude api keys to adress an issue with a publisher which inject custom js in the webview
  // we disable sentry on this publisher and on 12345 SSP to prevent many errors sended in sentry
  if (
    [
      '273976',
      '274316',
      '274389',
      '274390',
      '274391',
      '274392',
      '274393',
      '274394',
      '274395',
      '274493',
      '274494',
      '274888',
      '274889',
      '275752',
      '275753',
      '275864',
      '275915',
      '275929',
      '275930',
      '275955',
      '275956',
      '275957',
      '275958',
      '275970',
      '275971',
      '275972',
      '275973',
      '276000'
    ].indexOf(context?.apiKey || '') > -1 ||
    ['12345'].indexOf(context?._appExchange || '') > -1
  ) {
    return;
  }

  Raven = require('raven-js');

  if (!envVars.SENTRY_URL_UNIVERSAL_FORMATS || !envVars.COMMIT_HASH) {
    console.log(
      'SENTRY_URL_UNIVERSAL_FORMATS and/or COMMIT_HASH env variables not found. Sentry monitoring not initialized.'
    );
    return;
  }

  tags = {
    ...context,
    format,
    sdkMajorVersion: getMajorVersion(context?.sdkVersion),
    _delivery: isExternalSdk() ? 'external' : 'internal'
  };
  // Don't send AAID to Sentry
  delete tags.userId;
  // Don't send the user instance token to Sentry
  delete tags.userInstanceToken;
  // Don't send gdpr informations
  delete tags.gdpr;
  delete tags.tcfv2;

  const sentryURL = getSentryUrl(format);

  if (!sentryURL) {
    return;
  }

  try {
    Raven.config(sentryURL, {
      release: envVars.COMMIT_HASH,
      sampleRate: getSampleRate(format),
      ignoreErrors: getSentryIgnoreErrors(),
      ignoreUrls: getSentryIgnoreUrls(),
      environment: envVars.ENV,
      shouldSendCallback: () => shouldSendDatas,
      dataCallback: (data: any) => {
        data.tags = {
          ...data.tags,
          ...mraidInfos
        };

        if (
          data &&
          data.request &&
          data.request.url &&
          !isFormatError(data.request.url)
        ) {
          // this method does not exists in the doc
          // it reconfigure the Raven project endpoint
          Raven.setDSN(envVars.SENTRY_URL_CLIENT_SCRIPTS);
        }

        return data;
      },
      tags
    }).install();
  } catch (e) {
    DebugLog.log('Sentry init failed', { error: e });
  }
}

export function stopRaven(): void {
  shouldSendDatas = false;
}

export function logError(error: Error): void {
  if (!Raven) {
    return;
  }
  Raven.captureException(error);
}

export function logMessage(
  message: string,
  tags?: Record<string, any>,
  data?: string | Record<string, any>
): void {
  DebugLog.log(message, { tags, data });

  if (!Raven) {
    return;
  }

  if (data) {
    Raven.setExtraContext({ data: data });
  }
  if (tags) {
    Raven.setTagsContext(tags);
  }

  Raven.captureMessage(message);
}

export function updateMraidInfos(updates: {
  state?: string;
  isViewable?: boolean;
}): void {
  mraidInfos.mraidState = updates.state || mraidInfos.mraidState || 'unknown';
  mraidInfos.isViewable =
    typeof updates.isViewable !== 'undefined'
      ? String(updates.isViewable)
      : mraidInfos.isViewable || 'unknown';
}

export const __test__ = {
  isFormatError,
  getMajorVersion
};
